import type { AxiosInstance } from "axios";

/**
 * Plugin Control — the store's tracking switch, as the Integrations screen's Ecomally Plugin tab
 * sees it.
 *
 * Mirrors the API's `tracking-control` module rather than importing it (apps/web cannot bundle
 * server code — the same trade lib/team.ts makes). Switching off is the store's `paused` status:
 * Ecomally then records NOTHING from the store, orders included.
 */

export type TrackingSource = "merchant" | "ecomally";
export type TrackingBlock = "no_permission" | "org_paused" | "paused_by_ecomally";

export interface TrackingHistoryEntry {
  readonly id: string;
  readonly status: "active" | "paused";
  readonly source: TrackingSource;
  readonly actorName: string | null;
  readonly actorEmail: string | null;
  readonly reason: string | null;
  readonly at: string;
}

/** What is actually happening to the store's data (packages/health tracking-state.ts). */
export type TrackingState =
  | "receiving"
  | "disabled"
  | "paused_by_ecomally"
  | "uninstalled"
  | "not_started"
  | "stopped"
  | "unknown";

export interface TrackingReading {
  readonly state: TrackingState;
  readonly since: string | null;
  readonly ordersWithoutVisits: number;
}

/** One store on the warning banner (`GET /api/v2/tracking/stores`). */
export interface StoreTracking extends TrackingReading {
  readonly storeId: string;
  readonly displayName: string;
  readonly shopDomain: string;
}

export interface TrackingStatus {
  readonly enabled: boolean;
  readonly storeStatus: "active" | "paused";
  readonly orgPaused: boolean;
  readonly pausedBy: TrackingSource | null;
  readonly canControl: boolean;
  readonly blockedBy: TrackingBlock | null;
  readonly tracking: TrackingReading;
  readonly history: readonly TrackingHistoryEntry[];
}

/** The API's bounds on a reason (tracking-control.service.ts). */
export const REASON_MIN_LENGTH = 10;
export const REASON_MAX_LENGTH = 500;

/** How long the gateway may keep accepting data after a switch-off (its store cache). */
export const TAKES_EFFECT_MINUTES = 5;

const UNKNOWN: TrackingReading = { state: "unknown", since: null, ordersWithoutVisits: 0 };

/**
 * An answer without `tracking` (an API from before part 2 — the web can be deployed first) reads
 * as unknown rather than breaking the tab.
 */
function withReading(status: TrackingStatus): TrackingStatus {
  return { ...status, tracking: (status as Partial<TrackingStatus>).tracking ?? UNKNOWN };
}

export async function fetchTracking(client: AxiosInstance): Promise<TrackingStatus> {
  const response = await client.get<{ success: boolean; data: TrackingStatus }>("/api/v2/tracking");
  return withReading(response.data.data);
}

export async function fetchTrackingStores(client: AxiosInstance): Promise<readonly StoreTracking[]> {
  const response = await client.get<{ success: boolean; data: unknown }>("/api/v2/tracking/stores");
  // Anything but a list (an API without this route answering something else) is no warnings.
  return Array.isArray(response.data.data) ? (response.data.data as readonly StoreTracking[]) : [];
}

/**
 * Said on the window when a member switches tracking on or off, so the banner re-reads at once
 * rather than at its next interval.
 */
export const TRACKING_CHANGED_EVENT = "ecomally:tracking-changed";

export async function disableTracking(
  client: AxiosInstance,
  input: { readonly password: string; readonly reason: string },
): Promise<TrackingStatus> {
  const response = await client.post<{ success: boolean; data: TrackingStatus }>("/api/v2/tracking/disable", {
    ...input,
    confirm: true,
  });
  return withReading(response.data.data);
}

export async function enableTracking(client: AxiosInstance): Promise<TrackingStatus> {
  const response = await client.post<{ success: boolean; data: TrackingStatus }>("/api/v2/tracking/enable");
  return withReading(response.data.data);
}

export type TrackingTone = "ok" | "off" | "blocked";

export interface TrackingHeadline {
  readonly tone: TrackingTone;
  /** The badge. */
  readonly label: string;
  /** One sentence under it: what is happening to the store's data. */
  readonly detail: string;
}

/** Where to switch the embed on — said wherever the fix is "turn the embed on". */
const EMBED_PATH = "Online Store → Themes → Customize → App embeds";

/** "3 orders arrived with no visit recorded." — the evidence, or nothing when there is none. */
function evidence(orders: number): string {
  if (orders === 0) return "";
  return ` ${String(orders)} ${orders === 1 ? "order has" : "orders have"} arrived with no visit recorded.`;
}

/**
 * The sentence for a state that needs a warning — the banner's line and the Plugin tab's copy
 * read the same words. Null for receiving and unknown, which warn about nothing.
 */
export function stateMessage(reading: TrackingReading): { readonly tone: "red" | "amber"; readonly title: string; readonly body: string } | null {
  const since = reading.since === null ? null : formatChangeTime(reading.since);
  switch (reading.state) {
    case "stopped":
      return {
        tone: "red",
        title: "Tracking has stopped",
        body:
          `${since === null ? "No visits have arrived for over 48 hours." : `No visits have arrived since ${since}.`}` +
          `${evidence(reading.ordersWithoutVisits)} Check that the Ecomally app embed is still on in your Shopify theme (${EMBED_PATH}).`,
      };
    case "not_started":
      return {
        tone: "amber",
        title: "Tracking has not started",
        body: `No visit has arrived yet.${evidence(reading.ordersWithoutVisits)} Switch on the Ecomally app embed in your Shopify theme (${EMBED_PATH}).`,
      };
    case "uninstalled":
      return {
        tone: "red",
        title: "The Ecomally app was removed from Shopify",
        body: `${since === null ? "" : `Removed on ${since}. `}Ecomally no longer receives this store's data. Reinstall the app to resume.`,
      };
    case "disabled":
      return { tone: "amber", title: "Tracking is switched off", body: "It was switched off from this dashboard. Nothing from this store is being recorded." };
    case "paused_by_ecomally":
      return { tone: "amber", title: "Paused by Ecomally", body: "Nothing from this store is being recorded. Contact Ecomally support to resume it." };
    case "receiving":
    case "unknown":
      return null;
  }
}

/** What the status MEANS for the merchant's data — one place, so the badge and the copy agree. */
export function trackingHeadline(status: TrackingStatus): TrackingHeadline {
  if (status.orgPaused) {
    return {
      tone: "blocked",
      label: "Paused by Ecomally",
      detail: "Ecomally has paused your organization. No data is being recorded for any of its stores.",
    };
  }
  if (status.enabled) {
    // Switched on is not the same as receiving: say which.
    const message = stateMessage(status.tracking);
    if (status.tracking.state === "stopped" && message !== null) {
      return { tone: "blocked", label: "On, not receiving visits", detail: message.body };
    }
    if (status.tracking.state === "not_started" && message !== null) {
      return { tone: "off", label: "Waiting for the first visit", detail: message.body };
    }
    if (status.tracking.state === "unknown") {
      return {
        tone: "ok",
        label: "On",
        detail: "Switched on. We could not check just now whether visits are arriving — try again in a few minutes.",
      };
    }
    return { tone: "ok", label: "On", detail: "Ecomally is recording visits, orders and refunds from this store." };
  }
  if (status.pausedBy === "merchant") {
    return {
      tone: "off",
      label: "Off",
      detail: "Tracking was switched off from this dashboard. Nothing from this store is being recorded.",
    };
  }
  return {
    tone: "blocked",
    label: "Paused by Ecomally",
    detail: "Ecomally has paused this store. Nothing from it is being recorded until Ecomally resumes it.",
  };
}

/** Why the switch is not offered, in words — null when it is. */
export function blockedMessage(block: TrackingBlock | null): string | null {
  switch (block) {
    case "no_permission":
      return "Only an owner, or an admin an owner has allowed on the Team page, can switch tracking on or off.";
    case "org_paused":
    case "paused_by_ecomally":
      return "Contact Ecomally support to switch it back on.";
    case null:
      return null;
  }
}

/** "Switched off" / "Switched back on". */
export function changeLabel(entry: TrackingHistoryEntry): string {
  return entry.status === "paused" ? "Switched off" : "Switched back on";
}

/** The member's name, or "Ecomally team" for a staff change. */
export function changeActor(entry: TrackingHistoryEntry): string {
  if (entry.source === "ecomally") return "Ecomally team";
  return entry.actorName ?? "A former member";
}

/** "29 Oct 2026, 2:41 pm" in the viewer's zone. */
export function formatChangeTime(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** The disable form may be sent: a real reason, a password, and the box ticked. */
export function canSubmitDisable(input: { readonly reason: string; readonly password: string; readonly understood: boolean }): boolean {
  const reason = input.reason.trim().length;
  return input.understood && input.password.length > 0 && reason >= REASON_MIN_LENGTH && reason <= REASON_MAX_LENGTH;
}
